import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { CompanyCapabilities } from '@/domain/companies/schemas';

import { companiesApi } from '@/domain/companies/api';
import { queryOptions } from '@/services/query/queryOptions';
import { useApiMutation } from '@/services/query/useApiMutation';
import { showApiErrorToast } from '@/services/api/errorToast';
import toast from '@/components/Toast';
import { companyKeys } from '@/domain/companies/queries/keys';

export const companyCapabilitiesQuery = (companyId: string) =>
  queryOptions({
    queryKey: companyKeys.capabilities(companyId),
    queryFn: () => companiesApi.getCapabilities({ path: { companyId } }),
    staleTime: Infinity,
    // Only the company in scope is kept: the entry goes as soon as no route guard or component observes it.
    // A larger cacheTime from any other call would keep it.
    cacheTime: 0,
  });

export const useCompanyCapabilities = <TData = CompanyCapabilities>(
  companyId: string | null,
  { select }: { select?: (capabilities: CompanyCapabilities) => TData } = {},
) => useQuery({ ...companyCapabilitiesQuery(companyId ?? ''), enabled: Boolean(companyId), select });

// Refetch rather than write the answer into the cache: an answer cached for a company the user has already left
// would count as loaded on return, while the store still holds the other company's features.
export const useSetCompanyFeaturesMutation = (companyId: string) => {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: companiesApi.setFeatures,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: companyKeys.capabilities(companyId), exact: true });
      toast.success({ id: 'form.organization.features.update.success', defaultMessage: 'Settings updated' });
    },
    onError: (error) => showApiErrorToast(error),
  });
};
